import React, { useState } from 'react';
import { styled } from 'styled-components';
import { PERMISSIONS } from '../../../@types/Permissions';
import { withMatch } from '../../../contexts/MatchContext';
import { theme } from '../../templates/ui';
import { AuthorizedCan } from '../AuthorizedCan';
import Checkmark from '../Icons/Checkmark';
import Plus from '../Icons/Plus';

export const StyledPerson = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: ${(props) => {
    if (props.disabled) {
      return `${theme.sizing.large} ${theme.sizing.xlarge}`;
    }
    if (props.divergentStyling) {
      return `${theme.sizing.large}`;
    }

    return `${theme.sizing.large} 0`;
  }};
  border-bottom: 1px dashed ${theme.color.gray.light};
  margin: ${({ disabled }) => (disabled ? `0 calc(0px - ${theme.sizing.xlarge})` : '0')};
  background: ${({ disabled }) => (disabled ? 'rgba(0, 0, 0, .025);' : 'transparent')};
  background-color: ${(props) =>
    props.divergentStyling ? theme.color.gray.lighter : 'transparent'};

  &:last-of-type {
    border-bottom: none;
  }
`;

const Name = styled.h4`
  margin: 0;
  padding: 3px 0;
  flex: 1;

  ${({ checked }) =>
    checked &&
    `
    color: ${theme.color.base.base};
  `};
`;

const CheckboxWrapper = styled.div`
  input[type='checkbox'] {
    display: none;
  }

  input[type='checkbox'] + label {
    position: relative;
    display: block;
    width: 32px;
    height: 32px;
    border: 1px solid #d1d1d1;
    border-radius: 50%;
    color: ${theme.color.gray.black};
    text-align: center;
    transition: 0.1s all ease-in-out;

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;

      &:first-child {
        opacity: 1;
      }
    }

    &::before {
      transition: all 0.2s ease-in-out;
      border-radius: 2px;
      left: 0;
      top: -3px;
      width: ${theme.sizing.xxlarge};
      height: ${theme.sizing.xxlarge};
      border: 2px solid lightgrey;
      background-color: transparent;
    }
    &::after {
      transition: opacity 0.2s ease-in-out;
      position: absolute;
      display: block;
      content: '';
      height: 14px;
      width: 14px;
      opacity: 0;
      left: 3px;
      top: 2px;
      z-index: 2;
    }
  }

  input[type='checkbox']:checked + label {
    background-color: ${theme.color.secondary.base};
    border: 1px solid ${theme.color.secondary.base};

    svg {
      opacity: 1;

      &:first-child {
        opacity: 0;
      }
    }

    &::before {
      transition: all 0.2s ease-in-out;
      border: 2px solid ${theme.color.gray.black};
    }

    &::after {
      opacity: 1;
    }
  }
`;

const StyledPlus = styled(Plus)`
  fill: ${theme.color.gray.darkest};
  width: 15px;
  height: 15px;
`;

const StyledCheckmark = styled(Checkmark)`
  fill: ${theme.color.gray.white};
`;

const PersonComponent = ({
  teamId,
  name,
  className,
  checkboxId,
  clubMemberId,
  personId,
  matchUuid,
  disabled,
  divergentStyling,
  onAddSupport,
  onRemoveSupport,
}) => {
  const [checked, setChecked] = useState(false);

  const handleChangePersonState = async (matchUuid, teamId, clubMemberId, personId) => {
    if (!checked) {
      onAddSupport(matchUuid, clubMemberId, teamId, personId);
    } else {
      onRemoveSupport(matchUuid, clubMemberId, teamId, personId);
    }

    setChecked(!checked);
  };

  return (
    <StyledPerson
      className={className}
      disabled={disabled}
      divergentStyling={divergentStyling}
      data-testid={`person-${clubMemberId}`}
    >
      <Name checked={checked}>{name}</Name>
      <AuthorizedCan has={[PERMISSIONS.MANAGE_ALL_TEAMS_SUPPORT]} teamId={teamId}>
        <CheckboxWrapper>
          <input
            data-testid={`togglePerson-${clubMemberId}`}
            type="checkbox"
            id={checkboxId}
            onChange={() => {
              handleChangePersonState(matchUuid, teamId, clubMemberId, personId);
            }}
          />
          <label htmlFor={checkboxId} data-testid={`togglePerson-${clubMemberId}-label`}>
            <StyledPlus />
            <StyledCheckmark />
          </label>
        </CheckboxWrapper>
      </AuthorizedCan>
    </StyledPerson>
  );
};

export const Person = withMatch(PersonComponent);
